import moment from 'moment'

const DATE_ONLY_FORMAT = 'YYYY-MM-DD'

export type DateOnlyInput = string | Date

function toDateOnlyMoment(value: DateOnlyInput): moment.Moment {
  if (value instanceof Date) {
    const date = moment(value)

    if (!date.isValid()) {
      throw new Error('Invalid date value')
    }

    return date
  }

  const date = moment(value, DATE_ONLY_FORMAT, true)

  if (!date.isValid()) {
    throw new Error(`Expected date-only value in ${DATE_ONLY_FORMAT} format`)
  }

  return date
}

export function toInclusiveDate(exclusiveDate: DateOnlyInput): string {
  return toDateOnlyMoment(exclusiveDate)
    .subtract(1, 'day')
    .format(DATE_ONLY_FORMAT)
}

export function toExclusiveDate(inclusiveDate: DateOnlyInput): string {
  return toDateOnlyMoment(inclusiveDate).add(1, 'day').format(DATE_ONLY_FORMAT)
}

export function formatDateRange(date1Str: string, date2Str: string): string {
  const date1 = moment(date1Str)
  const date2 = moment(date2Str)

  const start = date1 < date2 ? date1 : date2
  const end = date1 >= date2 ? date1 : date2

  if (start.format('YYYY-MM') === end.format('YYYY-MM')) {
    // Same month and year
    return `${start.format('DD MMMM')} - ${end.format('D YYYY')}`
  } else if (start.format('YYYY') === end.format('YYYY')) {
    // Same year, different month
    return `${start.format('DD MMMM')} - ${end.format('DD MMMM YYYY')}`
  } else {
    // Different month, different year
    return `${start.format('DD MMMM YYYY')} - ${end.format('DD MMMM YYYY')}`
  }
}

export function toDateOnlyString(date: string | Date | undefined | null): string | undefined {
  if (date == null) return undefined

  if (typeof date === 'string') return date

  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')

  return `${date.getFullYear()}-${month}-${day}`
}

type DateOnlyLike =
  | string
  | {
      year?: number
      month?: number
      day?: number
    }

export function dateOnlyToDate(value?: DateOnlyLike | null): Date | null {
  if (!value) return null

  if (typeof value === 'string') {
    const [year, month, day] = value.split('-').map(Number)
    if (!year || !month || !day) return null

    return new Date(year, month - 1, day)
  }

  if (!value.year || !value.month || !value.day) return null

  return new Date(value.year, value.month - 1, value.day)
}

export function formatDateOnly(value?: DateOnlyLike | null): string | null {
  const date = dateOnlyToDate(value)
  if (!date) return null

  return moment(date).format('DD MMM YYYY')
}
